import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { studentApi } from '../../services/api';
import FilterPills from '../../components/FilterPills';

/**
 * R-1 / R-2 / R-3 — Three distinct reports surfaced as tabs:
 *
 *   R-1  "Sistem Performans Raporu" — auto from books + homework in-system
 *   R-2  "Dış Kayıt Raporu"         — student-entered Günlük Veri + Diğer Ödev metadata
 *   R-3  "Birleşik Rapor"           — merged view of both
 *
 * Data sources are kept orthogonal so Combined doesn't double-count:
 *   - R-1 reads studentApi.performance() + studentApi.assignments() (only
 *     book-test / system-scored homework counts).
 *   - R-2 reads the DD-1 localStorage bucket + "other homework" assignment
 *     rows (assignment_type === 'other') which carry the [OTHER:...] metadata
 *     the teacher entered in HW-1.
 */

type Tab = 'digital' | 'external' | 'combined';

interface SystemPerf {
  correct: number;
  wrong: number;
  accuracyPct: number;
  testCount: number;
  homeworkTotal: number;
  homeworkCompleted: number;
  homeworkCompletionPct: number;
}

interface ExternalPerf {
  dailyEntries: number;
  totalStudyMinutes: number;
  totalQuestionsSolved: number;
  otherHomeworkCount: number;
  otherHomeworkQuestions: number; // sum of questionCount fields where provided
}

interface DailyEntry {
  date: string;
  studyMinutes: number;
  questionsSolved: number;
  mood: string;
  notes: string;
}

// Daily entries moved from localStorage → student_daily_entries table
// (see DailyData.tsx + /student/daily-entries). Reports now reads from the
// API so cross-device + post-cache-clear data still shows up here.

const StatCard: React.FC<{ label: string; value: string | number; color?: string; sublabel?: string }> = ({ label, value, color = '#2b2b2b', sublabel }) => (
  <div className="bg-white rounded-[14px] p-4 text-center border border-gray-100">
    <p className="text-[12px] text-gray-500">{label}</p>
    <p className="text-[24px] font-bold" style={{ color }}>{value}</p>
    {sublabel && <p className="text-[11px] text-gray-400 mt-1">{sublabel}</p>}
  </div>
);

const StudentReports: React.FC = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('digital');
  const [loading, setLoading] = useState(true);

  const [systemPerf, setSystemPerf] = useState<SystemPerf>({
    correct: 0, wrong: 0, accuracyPct: 0, testCount: 0,
    homeworkTotal: 0, homeworkCompleted: 0, homeworkCompletionPct: 0,
  });
  const [external, setExternal] = useState<ExternalPerf>({
    dailyEntries: 0, totalStudyMinutes: 0, totalQuestionsSolved: 0,
    otherHomeworkCount: 0, otherHomeworkQuestions: 0,
  });

  useEffect(() => {
    setLoading(true);
    Promise.all([
      studentApi.performance('all'),
      studentApi.assignments(),
      studentApi.listDailyEntries(),
    ])
      .then(([perfRes, asRes, dailyRes]) => {
        const d: any = perfRes.data ?? {};
        const rows: any[] = asRes.data ?? [];

        // R-1: system metrics
        const completedStatuses = new Set(['submitted', 'graded', 'returned']);
        // "Other homework" is assignment_type === 'other' — exclude from the
        // digital (system) completion stat because R-2 owns it.
        const systemRows = rows.filter(r => r.assignment?.assignment_type !== 'other');
        const systemCompleted = systemRows.filter(r => completedStatuses.has(r.status)).length;
        setSystemPerf({
          correct: d.stats?.correct ?? 0,
          wrong: d.stats?.wrong ?? 0,
          accuracyPct: d.stats?.accuracy_pct ?? 0,
          testCount: d.stats?.attempts_count ?? 0,
          homeworkTotal: systemRows.length,
          homeworkCompleted: systemCompleted,
          homeworkCompletionPct: systemRows.length > 0
            ? Math.round((systemCompleted / systemRows.length) * 100)
            : 0,
        });

        // R-2: external — Günlük Veri (DB) + Diğer Ödev
        const otherRows = rows.filter(r => r.assignment?.assignment_type === 'other');
        const otherQuestionCount = otherRows.reduce((sum, r) => {
          const n = parseInt(r.assignment?.other_fields?.questionCount ?? '', 10);
          return sum + (isFinite(n) ? n : 0);
        }, 0);
        const daily = (dailyRes.data ?? []).map((e: any) => ({
          studyMinutes: e.study_minutes ?? 0,
          questionsSolved: e.questions_solved ?? 0,
        }));
        setExternal({
          dailyEntries: daily.length,
          totalStudyMinutes: daily.reduce((s: number, e: any) => s + (e.studyMinutes || 0), 0),
          totalQuestionsSolved: daily.reduce((s: number, e: any) => s + (e.questionsSolved || 0), 0),
          otherHomeworkCount: otherRows.length,
          otherHomeworkQuestions: otherQuestionCount,
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user?.id]);

  const combinedTotals = useMemo(() => {
    const totalQuestions = systemPerf.correct + systemPerf.wrong + external.totalQuestionsSolved + external.otherHomeworkQuestions;
    const totalHomework = systemPerf.homeworkTotal + external.otherHomeworkCount;
    return { totalQuestions, totalHomework };
  }, [systemPerf, external]);

  return (
    <div>
      <div className="mb-5">
        <FilterPills
          label="Rapor"
          tone="blue"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'digital', label: 'Sistem Raporu' },
            { value: 'external', label: 'Dış Kayıt Raporu' },
            { value: 'combined', label: 'Birleşik Rapor' },
          ]}
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : tab === 'digital' ? (
        <div>
          <p className="text-[13px] text-gray-500 mb-4">
            Kitaplar ve sistemdeki ödevlerden otomatik oluşan performans özeti.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard label="Doğru" value={systemPerf.correct} color="#59e56e" />
            <StatCard label="Yanlış" value={systemPerf.wrong} color="#e53e3e" />
            <StatCard label="Başarı Oranı" value={`%${systemPerf.accuracyPct}`} color="#3973bf" />
            <StatCard label="Toplam Test" value={systemPerf.testCount} color="#2b2b2b" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <StatCard label="Sistem Ödevi" value={systemPerf.homeworkTotal} />
            <StatCard label="Tamamlanan" value={systemPerf.homeworkCompleted} color="#59e56e" />
            <StatCard label="Tamamlama" value={`%${systemPerf.homeworkCompletionPct}`} color="#3973bf" />
          </div>
        </div>
      ) : tab === 'external' ? (
        <div>
          <p className="text-[13px] text-gray-500 mb-4">
            Öğrencinin girdiği Günlük Veriler ve "Diğer Ödev" kapsamındaki çalışmalar.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            <StatCard label="Günlük Kayıt" value={external.dailyEntries} />
            <StatCard label="Toplam Çalışma" value={`${external.totalStudyMinutes} dk`} color="#3973bf" />
            <StatCard label="Çözülen Soru (kişisel)" value={external.totalQuestionsSolved} color="#e5599b" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <StatCard label="Diğer Ödev" value={external.otherHomeworkCount} />
            <StatCard
              label="Diğer Ödev Toplam Soru"
              value={external.otherHomeworkQuestions}
              sublabel="Öğretmenin belirttiği soru sayılarının toplamı"
            />
          </div>
          {external.dailyEntries === 0 && external.otherHomeworkCount === 0 && (
            <p className="text-center text-gray-400 py-8">
              Henüz dış kayıt yok. Günlük Verilerim sayfasından kayıt ekleyebilirsiniz.
            </p>
          )}
        </div>
      ) : (
        <div>
          <p className="text-[13px] text-gray-500 mb-4">
            Sistem raporu ve dış kayıt raporu birlikte.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <StatCard label="Sistem Doğru" value={systemPerf.correct} color="#59e56e" />
            <StatCard label="Sistem Yanlış" value={systemPerf.wrong} color="#e53e3e" />
            <StatCard label="Başarı Oranı" value={`%${systemPerf.accuracyPct}`} color="#3973bf" />
            <StatCard label="Toplam Test" value={systemPerf.testCount} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
            <StatCard label="Günlük Kayıt" value={external.dailyEntries} />
            <StatCard label="Kişisel Çalışma" value={`${external.totalStudyMinutes} dk`} color="#3973bf" />
            <StatCard label="Kişisel Soru" value={external.totalQuestionsSolved} color="#e5599b" />
          </div>
          <div className="bg-[#fafafa] rounded-[16px] p-5 border border-gray-100">
            <p className="text-[13px] font-semibold text-[#3973bf] mb-3">Birleşik Toplamlar</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <StatCard
                label="Toplam Soru (sistem + kişisel)"
                value={combinedTotals.totalQuestions}
                color="#2b2b2b"
              />
              <StatCard
                label="Toplam Ödev (sistem + diğer)"
                value={combinedTotals.totalHomework}
                color="#2b2b2b"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentReports;
